import { env } from '@/config/env';

/**
 * Реклама за вознаграждение — клиентская сторона.
 *
 * Здесь нет ни одного решения: доступность, размер награды и сам факт
 * начисления определяет сервер. Клиент сообщает, что показ начался и
 * закончился, а кредит появляется только после серверной верификации
 * у провайдера.
 */
export interface RewardedAdAvailability {
  available: boolean;
  remaining: number;
  reward: number;
  /** false — работает заглушка, реальная реклама не подключена. */
  realAd: boolean;
}

export interface RewardedAdStart {
  eventId: string;
  reward: number;
  realAd: boolean;
}

export interface RewardedAdCompletion {
  granted: boolean;
  creditsLeft: number;
  reward: number;
  realAd: boolean;
}

/** Ошибка, пригодная для показа: текст пишет сервер, код — для ветвления. */
export class RewardedAdError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'RewardedAdError';
    this.code = code;
  }
}

interface ServerError {
  error?: { code?: string; message?: string };
}

async function call<T>(body: Record<string, unknown>, accessToken: string): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${env.VITE_SUPABASE_URL}/functions/v1/rewarded-ad`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new RewardedAdError(
      'Нет связи с сервером. Проверьте интернет и попробуйте снова.',
      'NETWORK_ERROR',
    );
  }

  const payload = (await response.json().catch(() => null)) as (T & ServerError) | null;

  if (!payload) {
    throw new RewardedAdError('Сервер ответил неожиданно. Попробуйте ещё раз.', 'UNKNOWN_ERROR');
  }

  if (!response.ok || payload.error) {
    throw new RewardedAdError(
      payload.error?.message ?? 'Не удалось получить награду. Попробуйте позже.',
      payload.error?.code ?? 'UNKNOWN_ERROR',
    );
  }

  return payload;
}

export function fetchRewardedAdAvailability(accessToken: string): Promise<RewardedAdAvailability> {
  return call<RewardedAdAvailability>({ action: 'availability' }, accessToken);
}

export function startRewardedAd(accessToken: string): Promise<RewardedAdStart> {
  return call<RewardedAdStart>({ action: 'start' }, accessToken);
}

export function completeRewardedAd(
  input: { eventId: string; providerEventId: string | null },
  accessToken: string,
): Promise<RewardedAdCompletion> {
  return call<RewardedAdCompletion>(
    { action: 'complete', eventId: input.eventId, providerEventId: input.providerEventId },
    accessToken,
  );
}
